import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

/**
 * Count of open maintenance reports (squawks) for an org, kept live via the
 * maintenance realtime channel. Used for the Maintenance nav badge (OMN-284).
 */
export function useOpenSquawkCount(orgId?: string): number {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!orgId) {
      setCount(0);
      return;
    }
    try {
      const { count: c, error } = await supabase
        .from('maintenance_reports')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .eq('status', 'open');
      if (!error) setCount(c ?? 0);
    } catch (err) {
      logger.debug('[useOpenSquawkCount] count fetch failed', err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Live-update the badge as squawks are filed/resolved in any session.
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  useEffect(() => {
    if (!orgId) return;
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    const channel = supabase
      .channel(`squawk-count:${orgId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'maintenance_reports', filter: `org_id=eq.${orgId}` },
        () => { fetchCount(); })
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [orgId, fetchCount]);

  return count;
}
